/**
 * RestoMod Studio - 2D Vehicle Stance, Wheel & Paint Canvas Rendering Engine
 *
 * Implements:
 * - Layered 2D Canvas pipeline (Ground & Shadow -> Wheels -> Body -> Paint Shaders -> HUD)
 * - Period vehicle silhouettes (Classic Muscle, Shop Truck, Deuce Roadster, Custom Upload)
 * - 3 Paint finish physics models (Acrylic Lacquer, Satin Primer, Metallic Enamel)
 * - 4 Wheel archetypes (Slotted Mags, Steelies, Torq Thrusts, Cross-Lace Mesh) across 15"-20"
 * - Constant 27.0" rolling tire diameter with realistic sidewalls & dressings
 * - Affine transformation kinematics for suspension drop, rake angle, and natural fender tuck
 * - Dynamic ground shadow tightening with ride height
 * - High-DPI Retina scaling and snapshot export
 */

import {
  VisualizerState,
  WheelArchetype,
  WheelDiameter,
  WheelFinish,
  TireTreatment,
  SILHOUETTES,
  SilhouetteData,
} from '../types/vehicle';

export const CANVAS_VIRTUAL_WIDTH = 1000;
export const CANVAS_VIRTUAL_HEIGHT = 500;
export const GROUND_Y = 400;
export const ROLLING_TIRE_RADIUS = 60; // 27.0" rolling diameter at ~4.444 px/inch
export const BASE_HUB_Y = GROUND_Y - ROLLING_TIRE_RADIUS; // 340px
export const DEFAULT_PIXELS_PER_INCH = 4.444;

export interface StanceParameters {
  wheelbase: number; // in pixels
  frontDrop: number; // in inches (-4.0 to +2.0)
  rearDrop: number;  // in inches (-4.0 to +2.0)
  scale: number;     // px/inch
}

export interface PitchAndTransforms {
  pitchAngleRad: number;
  pitchAngleDeg: number;
  frontHubYOffset: number; // in pixels
  rearHubYOffset: number;  // in pixels
  fenderClearanceFront: number; // in inches
  fenderClearanceRear: number;  // in inches
  rakeInches: number; // rearDrop - frontDrop
}

/**
 * Calculates suspension kinematics, rake pitch angle, and fender clearances
 */
export function calculatePitchAndTransforms(params: StanceParameters): PitchAndTransforms {
  const { wheelbase, frontDrop, rearDrop, scale } = params;

  // Negative drop lowers the car (moves body downward in canvas coordinates)
  // Positive drop lifts the car (moves body upward)
  const frontHubYOffset = -frontDrop * scale;
  const rearHubYOffset = -rearDrop * scale;

  // Rake delta: when rear is higher than front, rake is positive (nose-down aggressive stance)
  const rakeInches = rearDrop - frontDrop;

  // In canvas coords, body pitches nose-down when front is lower (more positive Y) than rear
  // Angle theta: counter-clockwise rotation around front hub lifts the rear
  const deltaY = rearHubYOffset - frontHubYOffset;
  const pitchAngleRad = Math.atan2(deltaY, wheelbase);
  const pitchAngleDeg = (pitchAngleRad * 180) / Math.PI;

  // Fender baseline clearance at stock (0 drop) is ~2.5 inches
  const stockGapInches = 2.5;
  const fenderClearanceFront = stockGapInches + frontDrop;
  const fenderClearanceRear = stockGapInches + rearDrop;

  return {
    pitchAngleRad,
    pitchAngleDeg,
    frontHubYOffset,
    rearHubYOffset,
    fenderClearanceFront,
    fenderClearanceRear,
    rakeInches,
  };
}

/**
 * Wheel rim radius for each wheel diameter (constant 27.0" rolling diameter)
 */
export function getWheelRimRadius(diameter: WheelDiameter): number {
  switch (diameter) {
    case 15:
      return 33.3; // 15" rim, 6.0" sidewall (26.7px)
    case 17:
      return 37.8; // 17" rim, 5.0" sidewall (22.2px)
    case 18:
      return 40.0; // 18" rim, 4.5" sidewall (20.0px)
    case 20:
      return 44.4; // 20" rim, 3.5" sidewall (15.6px)
    default:
      return 37.8;
  }
}

/**
 * Cached noise canvas for metallic fleck rendering to prevent jitter
 */
let cachedFleckPattern: CanvasPattern | null = null;

function getMetallicFleckPattern(ctx: CanvasRenderingContext2D): CanvasPattern | null {
  if (cachedFleckPattern) return cachedFleckPattern;

  if (typeof document === 'undefined') return null;
  const size = 128;
  const offscreen = document.createElement('canvas');
  offscreen.width = size;
  offscreen.height = size;
  const offCtx = offscreen.getContext('2d');
  if (!offCtx) return null;

  // Create high-frequency subtle metallic aluminum flakes
  const imgData = offCtx.createImageData(size, size);
  const data = imgData.data;
  for (let i = 0; i < data.length; i += 4) {
    const isFleck = Math.random() < 0.12;
    if (isFleck) {
      const brightness = Math.floor(180 + Math.random() * 75);
      data[i] = brightness;     // R
      data[i + 1] = brightness; // G
      data[i + 2] = brightness; // B
      data[i + 3] = Math.floor(40 + Math.random() * 100); // Alpha
    } else {
      data[i + 3] = 0; // Transparent
    }
  }
  offCtx.putImageData(imgData, 0, 0);
  cachedFleckPattern = ctx.createPattern(offscreen, 'repeat');
  return cachedFleckPattern;
}

/**
 * Finish color adjustments
 */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.substring(0, 2), 16) || 0;
  const g = parseInt(clean.substring(2, 4), 16) || 0;
  const b = parseInt(clean.substring(4, 6), 16) || 0;
  return { r, g, b };
}

function adjustBrightness(hex: string, percent: number): string {
  const { r, g, b } = hexToRgb(hex);
  const clamp = (val: number) => Math.min(255, Math.max(0, Math.round(val)));
  const nr = clamp(r + (r * percent) / 100);
  const ng = clamp(g + (g * percent) / 100);
  const nb = clamp(b + (b * percent) / 100);
  return `rgb(${nr}, ${ng}, ${nb})`;
}

// ----------------------------------------------------------------------------
// LAYER 0: GROUND PLANE & DYNAMIC GROUND SHADOW
// ----------------------------------------------------------------------------

export function drawGroundAndShadow(
  ctx: CanvasRenderingContext2D,
  _state: VisualizerState,
  frontHubX: number,
  rearHubX: number,
  kinematics: PitchAndTransforms
) {
  // 1. Concrete Workshop Floor
  const groundGradient = ctx.createLinearGradient(0, GROUND_Y, 0, CANVAS_VIRTUAL_HEIGHT);
  groundGradient.addColorStop(0, '#161c24');
  groundGradient.addColorStop(0.3, '#10141a');
  groundGradient.addColorStop(1, '#090d12');
  ctx.fillStyle = groundGradient;
  ctx.fillRect(0, GROUND_Y, CANVAS_VIRTUAL_WIDTH, CANVAS_VIRTUAL_HEIGHT - GROUND_Y);

  // Concrete floor joint expansion line
  ctx.strokeStyle = '#2b3644';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, GROUND_Y);
  ctx.lineTo(CANVAS_VIRTUAL_WIDTH, GROUND_Y);
  ctx.stroke();

  // Subtle workshop floor grid markers (60px spacing)
  ctx.strokeStyle = '#1e2632';
  ctx.lineWidth = 1;
  for (let x = 50; x < CANVAS_VIRTUAL_WIDTH; x += 80) {
    ctx.beginPath();
    ctx.moveTo(x, GROUND_Y);
    ctx.lineTo(x - 40, CANVAS_VIRTUAL_HEIGHT);
    ctx.stroke();
  }

  // 2. Dynamic Ground Occlusion Shadow
  // As clearance drops, shadow contracts and intensifies
  const minClearance = Math.min(kinematics.fenderClearanceFront, kinematics.fenderClearanceRear);
  const shadowCenter = (frontHubX + rearHubX) / 2;
  const shadowSpan = (rearHubX - frontHubX) * 0.75 + 140;

  // Tighter vertical spread when slammed
  const shadowHeight = Math.max(6, 12 + minClearance * 2.5);
  const shadowOpacity = Math.min(0.9, Math.max(0.4, 0.75 - minClearance * 0.08));

  ctx.save();
  ctx.beginPath();
  ctx.ellipse(shadowCenter, GROUND_Y + 1, shadowSpan, shadowHeight, 0, 0, Math.PI * 2);
  const shadowGrad = ctx.createRadialGradient(
    shadowCenter,
    GROUND_Y + 1,
    20,
    shadowCenter,
    GROUND_Y + 1,
    shadowSpan
  );
  shadowGrad.addColorStop(0, `rgba(0, 0, 0, ${shadowOpacity})`);
  shadowGrad.addColorStop(0.6, `rgba(0, 0, 0, ${shadowOpacity * 0.6})`);
  shadowGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = shadowGrad;
  ctx.fill();

  // Direct Tire Contact Patches
  const drawContactPatch = (x: number) => {
    ctx.beginPath();
    ctx.ellipse(x, GROUND_Y, 26, 4, 0, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(0, 0, 0, 0.95)';
    ctx.fill();
  };
  drawContactPatch(frontHubX);
  drawContactPatch(rearHubX);

  ctx.restore();
}

// ----------------------------------------------------------------------------
// LAYER 1 & 2: WHEEL & TIRE RENDERING
// ----------------------------------------------------------------------------

export function drawWheelAssembly(
  ctx: CanvasRenderingContext2D,
  hubX: number,
  hubY: number,
  diameter: WheelDiameter,
  archetype: WheelArchetype,
  finish: WheelFinish,
  tireTreatment: TireTreatment,
  hasBeautyRings?: boolean,
  hasRivetBolts?: boolean
) {
  const rRim = getWheelRimRadius(diameter);
  const rTire = ROLLING_TIRE_RADIUS;

  ctx.save();
  ctx.translate(hubX, hubY);

  // 1. Tire Rubber Outer Body
  ctx.beginPath();
  ctx.arc(0, 0, rTire, 0, Math.PI * 2);
  const tireGrad = ctx.createRadialGradient(0, 0, rRim, 0, 0, rTire);
  tireGrad.addColorStop(0, '#16181b');
  tireGrad.addColorStop(0.7, '#1f2327');
  tireGrad.addColorStop(0.95, '#121416');
  tireGrad.addColorStop(1, '#0b0c0e');
  ctx.fillStyle = tireGrad;
  ctx.fill();

  // Tread shoulder groove ring
  ctx.beginPath();
  ctx.arc(0, 0, rTire - 1.5, 0, Math.PI * 2);
  ctx.strokeStyle = '#0b0d0e';
  ctx.lineWidth = 1;
  ctx.stroke();

  // 2. Tire Dressings (White Letter, Redline, Whitewall, Goldline)
  if (tireTreatment === 'white_letter') {
    // Vintage Raised White Lettering (RADIAL T/A style arc dashes)
    ctx.save();
    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    // Upper arc text indicator
    ctx.arc(0, 0, (rRim + rTire) / 2, -Math.PI * 0.75, -Math.PI * 0.25);
    ctx.stroke();
    // Lower brand indicator
    ctx.beginPath();
    ctx.arc(0, 0, (rRim + rTire) / 2, Math.PI * 0.35, Math.PI * 0.65);
    ctx.stroke();
    // Authentic typography simulation
    ctx.font = 'bold 7px sans-serif';
    ctx.fillStyle = '#f8fafc';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('RADIAL T/A', 0, -((rRim + rTire) / 2));
    ctx.restore();
  } else if (tireTreatment === 'redline') {
    ctx.beginPath();
    ctx.arc(0, 0, (rRim + rTire) / 2, 0, Math.PI * 2);
    ctx.strokeStyle = '#dc2626';
    ctx.lineWidth = 2;
    ctx.stroke();
  } else if (tireTreatment === 'goldline') {
    ctx.beginPath();
    ctx.arc(0, 0, (rRim + rTire) / 2, 0, Math.PI * 2);
    ctx.strokeStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.stroke();
  }

  // 3. Brake Rotor & Performance Caliper (Visible behind open spokes)
  ctx.save();
  const rRotor = rRim * 0.82;
  ctx.beginPath();
  ctx.arc(0, 0, rRotor, 0, Math.PI * 2);
  const rotorGrad = ctx.createRadialGradient(0, 0, rRotor * 0.3, 0, 0, rRotor);
  rotorGrad.addColorStop(0, '#334155');
  rotorGrad.addColorStop(0.8, '#64748b');
  rotorGrad.addColorStop(1, '#475569');
  ctx.fillStyle = rotorGrad;
  ctx.fill();

  // Rotor cross-drilled cooling holes
  ctx.fillStyle = '#1e293b';
  for (let a = 0; a < Math.PI * 2; a += Math.PI / 6) {
    const holeX = Math.cos(a) * (rRotor * 0.68);
    const holeY = Math.sin(a) * (rRotor * 0.68);
    ctx.beginPath();
    ctx.arc(holeX, holeY, 1.2, 0, Math.PI * 2);
    ctx.fill();
  }

  // Performance Wilwood/Brembo Caliper (Top-forward mount)
  ctx.beginPath();
  ctx.arc(0, 0, rRotor * 0.96, -Math.PI * 0.95, -Math.PI * 0.55);
  ctx.strokeStyle = '#ef4444'; // Snap-on Red brake caliper
  ctx.lineWidth = 6;
  ctx.stroke();
  ctx.restore();

  // 4. Outer Rim Lip / Stepped Barrel
  ctx.beginPath();
  ctx.arc(0, 0, rRim, 0, Math.PI * 2);
  ctx.strokeStyle = '#cbd5e1';
  ctx.lineWidth = 2.5;
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(0, 0, rRim - 2, 0, Math.PI * 2);
  const lipGrad = ctx.createLinearGradient(-rRim, -rRim, rRim, rRim);
  lipGrad.addColorStop(0, '#ffffff');
  lipGrad.addColorStop(0.5, '#64748b');
  lipGrad.addColorStop(1, '#1e293b');
  ctx.strokeStyle = lipGrad;
  ctx.lineWidth = 2;
  ctx.stroke();

  // 5. Wheel Archetype Procedural Geometry
  const finishColors: Record<WheelFinish, { base: string; dark: string; light: string }> = {
    polished: { base: '#cbd5e1', dark: '#475569', light: '#ffffff' },
    machined_gray: { base: '#64748b', dark: '#334155', light: '#94a3b8' },
    matte_black: { base: '#18181b', dark: '#09090b', light: '#3f3f46' },
    chrome: { base: '#e2e8f0', dark: '#475569', light: '#ffffff' },
    gold: { base: '#d97706', dark: '#78350f', light: '#fbbf24' },
  };
  const fc = finishColors[finish] || finishColors.polished;

  switch (archetype) {
    case 'slotted_mags': {
      // 5 Kidney-Bean Slot Cutouts
      ctx.beginPath();
      ctx.arc(0, 0, rRim - 3, 0, Math.PI * 2);
      ctx.fillStyle = fc.base;
      ctx.fill();

      // Deep dish radial shadow
      const dishGrad = ctx.createRadialGradient(0, 0, 0, 0, 0, rRim - 3);
      dishGrad.addColorStop(0, 'rgba(0,0,0,0.5)');
      dishGrad.addColorStop(0.7, 'rgba(0,0,0,0.1)');
      dishGrad.addColorStop(1, 'rgba(255,255,255,0.3)');
      ctx.fillStyle = dishGrad;
      ctx.fill();

      // 5 Kidney slots
      ctx.fillStyle = '#0f172a';
      for (let i = 0; i < 5; i++) {
        const angle = (i * Math.PI * 2) / 5 - Math.PI / 2;
        ctx.save();
        ctx.rotate(angle);
        ctx.beginPath();
        // Slotted kidney shape
        ctx.ellipse(0, -(rRim * 0.58), rRim * 0.22, rRim * 0.12, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = fc.light;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.restore();
      }
      break;
    }

    case 'steelies': {
      // Solid stamped steel wheel face with center dog-dish cap
      ctx.beginPath();
      ctx.arc(0, 0, rRim - 3, 0, Math.PI * 2);
      ctx.fillStyle = fc.dark;
      ctx.fill();

      // Perimeter vent holes (10 small circle reliefs)
      ctx.fillStyle = '#090d12';
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI * 2) / 10;
        const vx = Math.cos(angle) * (rRim * 0.72);
        const vy = Math.sin(angle) * (rRim * 0.72);
        ctx.beginPath();
        ctx.arc(vx, vy, 2.5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Optional Chrome Beauty Ring
      if (hasBeautyRings) {
        ctx.beginPath();
        ctx.arc(0, 0, rRim - 1.5, 0, Math.PI * 2);
        ctx.strokeStyle = '#f8fafc';
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }

      // Chrome "Dog Dish" Poverty Cap (Center Dome)
      const rCap = rRim * 0.48;
      ctx.beginPath();
      ctx.arc(0, 0, rCap, 0, Math.PI * 2);
      const capGrad = ctx.createLinearGradient(-rCap, -rCap, rCap, rCap);
      capGrad.addColorStop(0, '#ffffff');
      capGrad.addColorStop(0.3, '#cbd5e1');
      capGrad.addColorStop(0.7, '#334155');
      capGrad.addColorStop(1, '#ffffff');
      ctx.fillStyle = capGrad;
      ctx.fill();
      ctx.strokeStyle = '#e2e8f0';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      break;
    }

    case 'torq_thrusts': {
      // 5 Classic D-Spokes radiating outward
      const numSpokes = 5;
      const spokeWidth = rRim * 0.28;

      for (let i = 0; i < numSpokes; i++) {
        const angle = (i * Math.PI * 2) / numSpokes - Math.PI / 2;
        ctx.save();
        ctx.rotate(angle);

        // Tapered D-Spoke
        ctx.beginPath();
        ctx.moveTo(-spokeWidth * 0.35, rRim * 0.2);
        ctx.lineTo(-spokeWidth * 0.5, rRim * 0.88);
        ctx.quadraticCurveTo(0, rRim * 0.94, spokeWidth * 0.5, rRim * 0.88);
        ctx.lineTo(spokeWidth * 0.35, rRim * 0.2);
        ctx.closePath();

        const spokeGrad = ctx.createLinearGradient(-spokeWidth, 0, spokeWidth, 0);
        spokeGrad.addColorStop(0, fc.light);
        spokeGrad.addColorStop(0.5, fc.base);
        spokeGrad.addColorStop(1, fc.dark);
        ctx.fillStyle = spokeGrad;
        ctx.fill();
        ctx.strokeStyle = '#1e293b';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.restore();
      }
      break;
    }

    case 'mesh': {
      // Multi-piece cross-lace mesh (16 interlocking diagonal spokes)
      ctx.beginPath();
      ctx.arc(0, 0, rRim - 3, 0, Math.PI * 2);
      ctx.fillStyle = '#090d12';
      ctx.fill();

      // Outer assembly rivets / perimeter micro-bolts
      if (hasRivetBolts) {
        ctx.fillStyle = '#f8fafc';
        for (let i = 0; i < 20; i++) {
          const a = (i * Math.PI * 2) / 20;
          const bx = Math.cos(a) * (rRim - 4);
          const by = Math.sin(a) * (rRim - 4);
          ctx.beginPath();
          ctx.arc(bx, by, 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Cross-hatch diamond spoke lines
      ctx.strokeStyle = fc.base;
      ctx.lineWidth = 1.8;
      const numRays = 16;
      for (let i = 0; i < numRays; i++) {
        const a1 = (i * Math.PI * 2) / numRays;
        const a2 = ((i + 2) * Math.PI * 2) / numRays;
        const a3 = ((i - 2) * Math.PI * 2) / numRays;

        const x1 = Math.cos(a1) * (rRim * 0.35);
        const y1 = Math.sin(a1) * (rRim * 0.35);
        const x2 = Math.cos(a2) * (rRim * 0.88);
        const y2 = Math.sin(a2) * (rRim * 0.88);
        const x3 = Math.cos(a3) * (rRim * 0.88);
        const y3 = Math.sin(a3) * (rRim * 0.88);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.moveTo(x1, y1);
        ctx.lineTo(x3, y3);
        ctx.stroke();
      }
      break;
    }
  }

  // 6. Central Hub Cap & Acorn Lug Nuts (Universal across styles)
  const rHub = rRim * 0.3;
  ctx.beginPath();
  ctx.arc(0, 0, rHub, 0, Math.PI * 2);
  const hubCapGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, rHub);
  hubCapGrad.addColorStop(0, '#f8fafc');
  hubCapGrad.addColorStop(0.7, '#64748b');
  hubCapGrad.addColorStop(1, '#1e293b');
  ctx.fillStyle = hubCapGrad;
  ctx.fill();
  ctx.strokeStyle = '#e2e8f0';
  ctx.lineWidth = 1;
  ctx.stroke();

  // 5 Chrome Acorn Lug Nuts
  const rLugs = rHub * 0.65;
  for (let i = 0; i < 5; i++) {
    const a = (i * Math.PI * 2) / 5 - Math.PI / 2;
    const lx = Math.cos(a) * rLugs;
    const ly = Math.sin(a) * rLugs;
    ctx.beginPath();
    ctx.arc(lx, ly, 2, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 0.8;
    ctx.stroke();
  }

  // Center bullet or spinner emblem
  ctx.beginPath();
  ctx.arc(0, 0, rHub * 0.25, 0, Math.PI * 2);
  ctx.fillStyle = '#f59e0b'; // Tachometer Amber center spinner tag
  ctx.fill();

  ctx.restore();
}

// ----------------------------------------------------------------------------
// LAYER 3 & 4: VEHICLE SILHOUETTES, PAINT & SHADERS
// ----------------------------------------------------------------------------

export function drawVehicleSilhouette(
  ctx: CanvasRenderingContext2D,
  state: VisualizerState,
  silhouette: SilhouetteData,
  kinematics: PitchAndTransforms,
  customImage?: HTMLImageElement | null
) {
  const { frontHubX, rearHubX, hubY0, frontArchRadius, rearArchRadius } = silhouette;

  ctx.save();

  // --------------------------------------------------------------------------
  // Apply Kinematic Affine Transformation to Vehicle Body
  // Rotate around front hub by rake angle theta, translate by suspension offsets
  // --------------------------------------------------------------------------
  ctx.translate(frontHubX, hubY0 + kinematics.frontHubYOffset);
  ctx.rotate(kinematics.pitchAngleRad);
  ctx.translate(-frontHubX, -hubY0);

  // --------------------------------------------------------------------------
  // Custom User Upload Profile
  // --------------------------------------------------------------------------
  if (state.silhouetteId === 'custom_upload' && customImage && customImage.complete) {
    const imgW = customImage.naturalWidth || 800;
    const imgH = customImage.naturalHeight || 400;
    const targetW = rearHubX - frontHubX + 320;
    const targetH = (targetW * imgH) / imgW;
    const targetX = frontHubX - 160;
    const targetY = hubY0 - targetH * 0.68;

    ctx.drawImage(customImage, targetX, targetY, targetW, targetH);
    ctx.restore();
    return;
  }

  // --------------------------------------------------------------------------
  // Procedural Vehicle Body Path Definition
  // --------------------------------------------------------------------------
  ctx.beginPath();

  if (state.silhouetteId === 'vintage_truck') {
    // 1967-72 C10 Fleetside Shop Truck
    ctx.moveTo(115, 360); // Front bumper bottom
    ctx.lineTo(105, 345); // Front bumper nose
    ctx.lineTo(110, 310); // Front grille lower
    ctx.lineTo(115, 290); // Front hood nose
    ctx.lineTo(340, 280); // Long flat truck hood
    ctx.lineTo(385, 195); // Sloped truck windshield
    ctx.lineTo(520, 195); // Horizontal cab roof
    ctx.lineTo(525, 280); // Upright rear cab wall
    ctx.lineTo(845, 280); // Pickup bed rail
    ctx.lineTo(855, 300); // Rear tailgate top corner
    ctx.lineTo(850, 350); // Rear tailgate
    ctx.lineTo(855, 360); // Rear bumper
    ctx.lineTo(835, 360); // Rear valance

    // Rear wheel arch cavity (radius ~78 around rear hub 755, 340)
    ctx.lineTo(rearHubX + rearArchRadius, 360);
    ctx.arc(rearHubX, hubY0, rearArchRadius, 0.25, Math.PI - 0.25, true);
    ctx.lineTo(rearHubX - rearArchRadius, 360);

    // Bed to cab rocker sill
    ctx.lineTo(frontHubX + frontArchRadius, 360);

    // Front wheel arch cavity (radius ~76 around front hub 245, 340)
    ctx.arc(frontHubX, hubY0, frontArchRadius, 0.25, Math.PI - 0.25, true);
    ctx.lineTo(frontHubX - frontArchRadius, 360);
    ctx.closePath();
  } else if (state.silhouetteId === 'deuce_roadster') {
    // 1932 Deuce Highboy Roadster / 3-Window Coupe
    ctx.moveTo(165, 345); // Front frame horn
    ctx.lineTo(195, 340); // Lower radiator shell
    ctx.lineTo(200, 255); // Vertical Deuce grille shell top
    ctx.lineTo(210, 255); // Radiator cap
    ctx.lineTo(350, 255); // Louvered hood top
    ctx.lineTo(405, 205); // Chopped raked roadster windshield
    ctx.lineTo(530, 205); // Roadster cockpit rail
    ctx.lineTo(600, 245); // Rear deck transition
    ctx.lineTo(760, 285); // Sweeping turtle deck
    ctx.lineTo(785, 335); // Rear tank / tail
    ctx.lineTo(775, 345); // Rear frame rail

    // Roadster highboy stance has exposed wheels with open body clearance
    ctx.lineTo(rearHubX + rearArchRadius, 345);
    ctx.arc(rearHubX, hubY0, rearArchRadius, 0.15, Math.PI - 0.15, true);
    ctx.lineTo(rearHubX - rearArchRadius, 345);

    // Center I-beam chassis frame rail
    ctx.lineTo(frontHubX + frontArchRadius, 345);
    ctx.arc(frontHubX, hubY0, frontArchRadius, 0.15, Math.PI - 0.15, true);
    ctx.lineTo(frontHubX - frontArchRadius, 345);
    ctx.closePath();
  } else {
    // Default: 1969 Classic Fastback Muscle Coupe (Charger / Mustang / Camaro)
    ctx.moveTo(125, 360); // Front chin spoiler
    ctx.lineTo(115, 340); // Front bumper chrome point
    ctx.lineTo(120, 315); // Recessed grille brow
    ctx.lineTo(135, 305); // Nose header
    ctx.lineTo(360, 275); // Swept long muscle hood
    ctx.lineTo(435, 215); // Raked fastback windshield
    ctx.lineTo(550, 215); // Low roof peak
    ctx.lineTo(740, 280); // Fastback sweeping C-pillar
    ctx.lineTo(825, 285); // Ducktail rear trunk lid
    ctx.lineTo(835, 300); // Ducktail lip spoiler
    ctx.lineTo(845, 335); // Rear tail panel & chrome bumper
    ctx.lineTo(830, 360); // Rear lower valance

    // Rear wheel arch cavity (radius ~74 around rear hub 740, 340)
    ctx.lineTo(rearHubX + rearArchRadius, 360);
    ctx.arc(rearHubX, hubY0, rearArchRadius, 0.25, Math.PI - 0.25, true);
    ctx.lineTo(rearHubX - rearArchRadius, 360);

    // Rocker sill between wheels
    ctx.lineTo(frontHubX + frontArchRadius, 360);

    // Front wheel arch cavity (radius ~72 around front hub 260, 340)
    ctx.arc(frontHubX, hubY0, frontArchRadius, 0.25, Math.PI - 0.25, true);
    ctx.lineTo(frontHubX - frontArchRadius, 360);
    ctx.closePath();
  }

  // --------------------------------------------------------------------------
  // Apply Paint Finish Physics Model (Fill Base Color)
  // --------------------------------------------------------------------------
  const { colorHex, finishType, clearcoatGloss, metallicFleckDensity } = state.paint;
  ctx.fillStyle = colorHex;
  ctx.fill();

  // --------------------------------------------------------------------------
  // Clearcoat & Specular Shader Overlays (Clipped to body)
  // --------------------------------------------------------------------------
  ctx.save();
  ctx.clip(); // Clip shaders strictly inside vehicle body path

  if (finishType === 'acrylic_lacquer') {
    // 1. High-Gloss Acrylic Lacquer:
    // Mirror sheen with sharp overhead shop light streaks and sill bounce
    const glossAlpha = Math.max(0.2, clearcoatGloss);

    // Overhead fluorescent tube highlight band 1 (Roof & Hood)
    const roofLight = ctx.createLinearGradient(100, 200, 800, 260);
    roofLight.addColorStop(0, `rgba(255, 255, 255, ${0.45 * glossAlpha})`);
    roofLight.addColorStop(0.3, `rgba(255, 255, 255, ${0.15 * glossAlpha})`);
    roofLight.addColorStop(0.7, 'rgba(255, 255, 255, 0)');
    ctx.fillStyle = roofLight;
    ctx.fillRect(100, 180, 750, 120);

    // Shoulder beltline specular streak (Crisp 2px bar)
    ctx.strokeStyle = `rgba(255, 255, 255, ${0.75 * glossAlpha})`;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(130, 310);
    ctx.lineTo(360, 280);
    ctx.lineTo(820, 290);
    ctx.stroke();

    // Lower sill ground reflection bounce
    const sillLight = ctx.createLinearGradient(0, 370, 0, 330);
    sillLight.addColorStop(0, `rgba(180, 210, 240, ${0.25 * glossAlpha})`);
    sillLight.addColorStop(1, 'rgba(180, 210, 240, 0)');
    ctx.fillStyle = sillLight;
    ctx.fillRect(100, 320, 750, 50);

  } else if (finishType === 'satin_primer') {
    // 2. Satin Primer:
    // Diffuse matte falloff with zero sharp specular glare
    const primerGrad = ctx.createLinearGradient(0, 200, 0, 370);
    primerGrad.addColorStop(0, 'rgba(255, 255, 255, 0.08)');
    primerGrad.addColorStop(0.5, 'rgba(0, 0, 0, 0)');
    primerGrad.addColorStop(1, 'rgba(0, 0, 0, 0.22)');
    ctx.fillStyle = primerGrad;
    ctx.fillRect(100, 180, 760, 190);

  } else if (finishType === 'metallic_enamel') {
    // 3. Metallic Enamel:
    // Microscopic aluminum flecks embedded in high-pigment resin + clearcoat
    const fleckPattern = getMetallicFleckPattern(ctx);
    if (fleckPattern && metallicFleckDensity > 0) {
      ctx.save();
      ctx.globalAlpha = Math.min(0.7, 0.2 + metallicFleckDensity * 0.5);
      ctx.fillStyle = fleckPattern;
      ctx.fillRect(100, 180, 760, 190);
      ctx.restore();
    }

    // Deep luminous hot spot centered on front quarter
    const hotspot = ctx.createRadialGradient(420, 250, 20, 420, 250, 320);
    hotspot.addColorStop(0, `rgba(255, 255, 255, ${0.45 * clearcoatGloss})`);
    hotspot.addColorStop(0.5, `rgba(255, 255, 255, ${0.1 * clearcoatGloss})`);
    hotspot.addColorStop(1, 'rgba(0, 0, 0, 0.2)');
    ctx.fillStyle = hotspot;
    ctx.fillRect(100, 180, 760, 190);
  }

  ctx.restore(); // Restore clip

  // --------------------------------------------------------------------------
  // Body Details: Windows, Chrome Trim, Shut Lines & Badges
  // --------------------------------------------------------------------------
  ctx.strokeStyle = '#2b3644';
  ctx.lineWidth = 1.2;

  if (state.silhouetteId === 'vintage_truck') {
    // Truck Cab Glass
    ctx.fillStyle = '#0f172a'; // Deep tinted shop glass
    ctx.beginPath();
    ctx.moveTo(395, 275);
    ctx.lineTo(395, 205);
    ctx.lineTo(515, 205);
    ctx.lineTo(515, 275);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#cbd5e1'; // Chrome window surround
    ctx.stroke();

    // B-Pillar divider
    ctx.beginPath();
    ctx.moveTo(465, 205);
    ctx.lineTo(465, 275);
    ctx.stroke();

    // Door shut lines
    ctx.strokeStyle = adjustBrightness(colorHex, -35);
    ctx.beginPath();
    ctx.moveTo(375, 280);
    ctx.lineTo(375, 360);
    ctx.moveTo(525, 280);
    ctx.lineTo(525, 360);
    ctx.stroke();

    // Chrome door handle
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(485, 290, 16, 3);

    // Chrome bumpers
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(100, 340, 14, 22); // Front heavy bumper
    ctx.fillRect(846, 340, 14, 22); // Rear step bumper

    // Amber front side marker
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(135, 310, 8, 4);

  } else if (state.silhouetteId === 'deuce_roadster') {
    // Roadster Windshield glass
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(365, 255);
    ctx.lineTo(405, 210);
    ctx.lineTo(415, 210);
    ctx.lineTo(380, 255);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = '#f8fafc'; // Chrome windshield frame
    ctx.stroke();

    // Vertical grille shell chrome border
    ctx.strokeStyle = '#f8fafc';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(195, 340);
    ctx.lineTo(200, 255);
    ctx.lineTo(210, 255);
    ctx.stroke();

    // Exposed Chrome Bucket Headlights mounted on frame
    ctx.fillStyle = '#f8fafc';
    ctx.beginPath();
    ctx.ellipse(225, 290, 10, 16, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Headlight amber lens glow
    ctx.fillStyle = 'rgba(245, 158, 11, 0.4)';
    ctx.beginPath();
    ctx.arc(220, 290, 7, 0, Math.PI * 2);
    ctx.fill();

  } else {
    // Muscle Car Windows & Trim
    ctx.fillStyle = '#0f172a';
    ctx.beginPath();
    ctx.moveTo(445, 272);
    ctx.lineTo(440, 222);
    ctx.lineTo(545, 222);
    ctx.lineTo(725, 275);
    ctx.closePath();
    ctx.fill();

    // Chrome window molding
    ctx.strokeStyle = '#cbd5e1';
    ctx.lineWidth = 1.8;
    ctx.stroke();

    // Quarter-window divider post
    ctx.beginPath();
    ctx.moveTo(560, 222);
    ctx.lineTo(560, 273);
    ctx.stroke();

    // Door shutline
    ctx.strokeStyle = adjustBrightness(colorHex, -35);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(435, 275);
    ctx.lineTo(435, 360);
    ctx.moveTo(570, 275);
    ctx.lineTo(570, 360);
    ctx.stroke();

    // Chrome door handle
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(530, 288, 14, 3);

    // Front & rear chrome bumpers
    ctx.fillStyle = '#e2e8f0';
    ctx.fillRect(110, 335, 12, 14); // Front bumper
    ctx.fillRect(835, 335, 12, 14); // Rear bumper

    // Amber front side marker & red rear marker
    ctx.fillStyle = '#f59e0b';
    ctx.fillRect(150, 315, 8, 4);
    ctx.fillStyle = '#ef4444';
    ctx.fillRect(810, 315, 8, 4);

    // Twin chrome exhaust tips
    ctx.fillStyle = '#cbd5e1';
    ctx.fillRect(825, 362, 16, 4);
  }

  // Exterior Sheet Metal Body Contour Edge Line
  ctx.strokeStyle = adjustBrightness(colorHex, -40);
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.restore();
}

// ----------------------------------------------------------------------------
// LAYER 5: TECHNICAL DIMENSION & CALIPER CAD HUD
// ----------------------------------------------------------------------------

export function formatHudRakeLabel(pitchAngleDeg: number): string {
  return pitchAngleDeg < -0.05
    ? `RAKE: +${Math.abs(pitchAngleDeg).toFixed(2)}° [NOSE-DOWN]`
    : pitchAngleDeg > 0.05
    ? `RAKE: -${Math.abs(pitchAngleDeg).toFixed(2)}° [NOSE-UP / GASSER]`
    : `RAKE: LEVEL (0.00°)`;
}

export function drawDimensionHud(
  ctx: CanvasRenderingContext2D,
  state: VisualizerState,
  silhouette: SilhouetteData,
  kinematics: PitchAndTransforms
) {
  const { frontHubX, rearHubX, hubY0 } = silhouette;
  const frontY = hubY0 + kinematics.frontHubYOffset;
  const rearY = hubY0 + kinematics.rearHubYOffset;

  ctx.save();

  // Blueprint Cyan Style
  ctx.strokeStyle = '#38bdf8';
  ctx.fillStyle = '#38bdf8';
  ctx.lineWidth = 1;
  ctx.font = '10px ui-monospace, SFMono-Regular, Menlo, monospace';

  // 1. Wheelbase Dimension Caliper Line
  const caliperY = GROUND_Y + 36;
  ctx.beginPath();
  // Leader lines from hubs down to caliper
  ctx.setLineDash([2, 2]);
  ctx.moveTo(frontHubX, BASE_HUB_Y);
  ctx.lineTo(frontHubX, caliperY + 8);
  ctx.moveTo(rearHubX, BASE_HUB_Y);
  ctx.lineTo(rearHubX, caliperY + 8);
  ctx.stroke();

  // Caliper horizontal bar
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(frontHubX, caliperY);
  ctx.lineTo(rearHubX, caliperY);
  ctx.stroke();

  // Caliper arrow ticks
  ctx.beginPath();
  ctx.moveTo(frontHubX + 4, caliperY - 3);
  ctx.lineTo(frontHubX, caliperY);
  ctx.lineTo(frontHubX + 4, caliperY + 3);
  ctx.moveTo(rearHubX - 4, caliperY - 3);
  ctx.lineTo(rearHubX, caliperY);
  ctx.lineTo(rearHubX - 4, caliperY + 3);
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.fillText(
    `WHEELBASE: ${silhouette.wheelbaseInches.toFixed(1)}" [${silhouette.wheelbasePx}px]`,
    (frontHubX + rearHubX) / 2,
    caliperY - 4
  );

  // 2. Hub Crosshairs
  const drawCrosshair = (x: number, y: number, label: string, offsetInches: number, clearance: number) => {
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(x, y, 6, 0, Math.PI * 2);
    ctx.moveTo(x - 12, y);
    ctx.lineTo(x + 12, y);
    ctx.moveTo(x, y - 12);
    ctx.lineTo(x, y + 12);
    ctx.stroke();

    ctx.textAlign = 'left';
    ctx.fillText(`${label}: ${offsetInches >= 0 ? '+' : ''}${offsetInches.toFixed(2)}"`, x + 16, y - 6);
    ctx.fillStyle = clearance < 0 ? '#ef4444' : '#94a3b8';
    ctx.fillText(`GAP: ${clearance.toFixed(2)}" ${clearance < 0 ? '[TUCKED]' : ''}`, x + 16, y + 6);
    ctx.fillStyle = '#38bdf8';
  };

  drawCrosshair(frontHubX, frontY, 'FRONT', state.stance.frontHeightOffsetInches, kinematics.fenderClearanceFront);
  drawCrosshair(rearHubX, rearY, 'REAR', state.stance.rearHeightOffsetInches, kinematics.fenderClearanceRear);

  // 3. Stance Rake Angle & Direction
  const rakeLabel = formatHudRakeLabel(kinematics.pitchAngleDeg);

  ctx.textAlign = 'center';
  ctx.fillText(rakeLabel, (frontHubX + rearHubX) / 2, 70);

  // 4. Ground Clearance Horizon Tag
  ctx.textAlign = 'right';
  ctx.fillStyle = '#64748b';
  ctx.fillText(`DATUM GROUND: Y=400`, CANVAS_VIRTUAL_WIDTH - 20, GROUND_Y - 6);

  ctx.restore();
}

// ----------------------------------------------------------------------------
// MASTER RENDER FUNCTION
// ----------------------------------------------------------------------------

export interface RenderOptions {
  customImage?: HTMLImageElement | null;
  pinningMode?: boolean;
}

export function renderVisualizerScene(
  canvas: HTMLCanvasElement,
  state: VisualizerState,
  options?: RenderOptions
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const displayW = CANVAS_VIRTUAL_WIDTH;
  const displayH = CANVAS_VIRTUAL_HEIGHT;

  if (canvas.width !== displayW * dpr || canvas.height !== displayH * dpr) {
    canvas.width = displayW * dpr;
    canvas.height = displayH * dpr;
  }

  ctx.save();
  ctx.scale(dpr, dpr);

  // Clear scene
  ctx.clearRect(0, 0, displayW, displayH);

  // Retrieve silhouette metadata
  const silhouetteId = state.silhouetteId === 'custom_upload' ? 'classic_muscle' : state.silhouetteId;
  const silhouette = SILHOUETTES[silhouetteId] || SILHOUETTES.classic_muscle;

  const frontHubX = silhouette.frontHubX;
  const rearHubX = silhouette.rearHubX;
  const wheelbasePx = rearHubX - frontHubX;

  // Calculate kinematics
  const kinematics = calculatePitchAndTransforms({
    wheelbase: wheelbasePx,
    frontDrop: state.stance.frontHeightOffsetInches,
    rearDrop: state.stance.rearHeightOffsetInches,
    scale: silhouette.scalePxPerInch,
  });

  // Layer 0: Ground plane & shadow
  drawGroundAndShadow(ctx, state, frontHubX, rearHubX, kinematics);

  // Layer 1 & 2: Wheels and Tires
  // Rear Wheel
  drawWheelAssembly(
    ctx,
    rearHubX,
    BASE_HUB_Y,
    state.stance.rearWheelDiameter,
    state.wheel.archetype,
    state.wheel.finish,
    state.wheel.tireTreatment,
    state.wheel.hasBeautyRings,
    state.wheel.hasRivetBolts
  );

  // Front Wheel
  drawWheelAssembly(
    ctx,
    frontHubX,
    BASE_HUB_Y,
    state.stance.frontWheelDiameter,
    state.wheel.archetype,
    state.wheel.finish,
    state.wheel.tireTreatment,
    state.wheel.hasBeautyRings,
    state.wheel.hasRivetBolts
  );

  // Layer 3 & 4: Vehicle Body Silhouette & Paint Shaders
  drawVehicleSilhouette(ctx, state, silhouette, kinematics, options?.customImage);

  // Layer 5: Technical Caliper Dimension HUD (Optional)
  if (state.showDimensionHud) {
    drawDimensionHud(ctx, state, silhouette, kinematics);
  }

  // Pinning Mode Crosshairs (When user is pinning custom hubs)
  if (options?.pinningMode) {
    ctx.save();
    ctx.strokeStyle = '#f59e0b';
    ctx.fillStyle = '#f59e0b';
    ctx.lineWidth = 2;
    ctx.font = 'bold 11px ui-monospace, monospace';

    const drawPin = (x: number, y: number, text: string) => {
      ctx.beginPath();
      ctx.arc(x, y, 10, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.textAlign = 'center';
      ctx.fillText(text, x, y - 16);
    };

    drawPin(frontHubX, BASE_HUB_Y, 'PIN: FRONT HUB');
    drawPin(rearHubX, BASE_HUB_Y, 'PIN: REAR HUB');
    ctx.restore();
  }

  ctx.restore();
}

/**
 * Exports current canvas as a high-resolution PNG data URL
 */
export function exportCanvasSnapshot(canvas: HTMLCanvasElement, filename = 'restomod-stance-snapshot.png'): string {
  const dataUrl = canvas.toDataURL('image/png');
  if (typeof document !== 'undefined') {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  return dataUrl;
}
